import { create } from "zustand";
import { persist } from "zustand/middleware";
import api from "../lib/api";
import useAuthStore from "./authStore";

/**
 * Client-side wishlist (localStorage) that also syncs to the server when
 * a customer is logged in — read from other stores via .getState() at
 * call time (not a hook dependency) so every existing toggle()/has()
 * call site (ProductCard, ProductDetail, Wishlist page) keeps working
 * unchanged. A guest's wishlist stays local-only, same as before.
 * See hooks/useWishlistSync.js for the login-time merge.
 */
const useWishlistStore = create(
  persist(
    (set, get) => ({
      productIds: [],
      toggle: (productId) => {
        const wasSaved = get().productIds.includes(productId);
        set((state) => ({
          productIds: wasSaved
            ? state.productIds.filter((id) => id !== productId)
            : [...state.productIds, productId],
        }));

        const { user } = useAuthStore.getState();
        if (user) {
          const request = wasSaved
            ? api.delete(`/wishlist/${productId}`)
            : api.post("/wishlist", { productId });
          // Best-effort — a failed sync leaves the local copy as the
          // source of truth for this session rather than blocking the UI.
          request.catch(() => {});
        }
      },
      has: (productId) => get().productIds.includes(productId),
      clear: () => set({ productIds: [] }),
      mergeIds: (ids) =>
        set((state) => ({ productIds: [...new Set([...state.productIds, ...ids])] })),
    }),
    { name: "sc-wishlist" }
  )
);

export default useWishlistStore;
